/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Spinner } from 'react-bootstrap';
import { MdCancel } from 'react-icons/md';
import { FiAlertTriangle } from 'react-icons/fi';
import { IoDocumentTextOutline, IoShieldCheckmarkOutline } from 'react-icons/io5'; // for the second warning
import CommonSelect from '../../common/CommonSelect';
import CommonButton from '../../common/CommonButton';
import { getAllAuditors, getTemplates } from '../../services/admin';
import { QUOTAS, TIERS, getSubscriptionTier } from '../../util/subscription';
import limitReachedIconImg from '../../assets/images/LimitReached.png';
import moment from 'moment';

const DowngradeModal = ({ show, onHide, targetPlan, currentPlan, currentPeriodEnd, onConfirm, compulsory = false, onUpgradeToPreviousPlan }) => {
  const [auditors, setAuditors] = useState([]);
  const [delegateAdmins, setDelegateAdmins] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedAuditors, setSelectedAuditors] = useState([]);
  const [selectedDelegateAdmins, setSelectedDelegateAdmins] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Quotas for target plan
  const targetTier = getSubscriptionTier(targetPlan?.name);
  const targetQuotas = QUOTAS[targetTier] || QUOTAS[TIERS.BRONZE];
  const maxAuditors = targetQuotas.auditors;
  const maxDelegateAdmins = targetQuotas.delegateAdmins || 0;
  const maxTemplates = targetQuotas.templateClones;

  // Quotas for current plan
  const currentTier = getSubscriptionTier(currentPlan?.name);
  const currentQuotas = QUOTAS[currentTier] || QUOTAS[TIERS.GOLD];

  useEffect(() => {
    const fetchResources = async () => {
      setLoadingResources(true);
      try {
        // Fetch active auditors
        const auditorsRes = await getAllAuditors(1, 100, 'active');
        const aResult = auditorsRes.data?.data || auditorsRes.data;
        const allUsers = Array.isArray(aResult) ? aResult : aResult?.items || [];

        const standardAuditors = allUsers.filter((u) => u.role !== 'Delegate Admin');
        const delegateAdminUsers = allUsers.filter((u) => u.role === 'Delegate Admin');

        const auditorOptions = standardAuditors.map((a) => ({
          value: a.id,
          label: a.full_name || a.name || a.email
        }));
        setAuditors(auditorOptions);
        // Start with no pre-selection — user must manually choose which auditors to retain
        setSelectedAuditors([]);

        const delegateOptions = delegateAdminUsers.map((a) => ({
          value: a.id,
          label: a.full_name || a.name || a.email
        }));
        setDelegateAdmins(delegateOptions);
        // Start with no pre-selection
        setSelectedDelegateAdmins([]);

        // Fetch active templates
        const templatesRes = await getTemplates(1, 10, '', true, 0); // Active templates
        const tResult = templatesRes.data?.data || templatesRes.data;
        const templatesData = Array.isArray(tResult) ? tResult : tResult?.items || [];

        const templateOptions = templatesData.map((t) => ({
          value: t.id,
          label: t.template_name || t.name
        }));
        setTemplates(templateOptions);
        // Start with no pre-selection — user must manually choose which templates to retain
        setSelectedTemplates([]);
      } catch (error) {
        console.error('Error fetching resources:', error);
      } finally {
        setLoadingResources(false);
      }
    };

    if (show) {
      fetchResources();
    } else {
      // Reset state when modal is closed
      setSelectedAuditors([]);
      setSelectedDelegateAdmins([]);
      setSelectedTemplates([]);
      setStep(1);
      setHasSubmitted(false);
    }
  }, [show, maxAuditors, maxDelegateAdmins, maxTemplates]);

  const handleAuditorChange = (e) => {
    const values = e.target.value;
    const newValues = typeof values === 'string' ? values.split(',') : values;
    if (newValues.length <= maxAuditors) {
      setSelectedAuditors(newValues);
    }
  };

  const handleDelegateAdminChange = (e) => {
    const values = e.target.value;
    const newValues = typeof values === 'string' ? values.split(',') : values;
    if (newValues.length <= maxDelegateAdmins) {
      setSelectedDelegateAdmins(newValues);
    }
  };

  const handleTemplateChange = (e) => {
    const values = e.target.value;
    const newValues = typeof values === 'string' ? values.split(',') : values;
    if (newValues.length <= maxTemplates) {
      setSelectedTemplates(newValues);
    }
  };

  const handleReviewDowngrade = () => {
    setStep(2);
  };

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm({
        planId: targetPlan?.id,
        retainedAuditors: selectedAuditors,
        retainedDelegateAdmins: selectedDelegateAdmins,
        retainedTemplates: selectedTemplates
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsProcessing(false);
      onHide();
    }
  };

  // Compare features to see what is lost
  const getLostFeatures = () => {
    const lost = [];
    if (currentQuotas.templateClones > maxTemplates) {
      lost.push(`Clone up to ${currentQuotas.templateClones} global templates`);
    }
    // Checking boolean permissions assuming they map to tier differences
    if (currentTier === TIERS.GOLD && targetTier !== TIERS.GOLD) {
      lost.push('Advanced charts');
      lost.push('Export CSV analytics');
    }
    if (currentQuotas.delegateAdmins > targetQuotas.delegateAdmins) {
      lost.push(`Create up to ${currentQuotas.delegateAdmins} delegate admin role(s)`);
    }
    return lost.length ? lost : ['Some advanced features associated with your current tier.'];
  };

  const limitExceeded = auditors.length > maxAuditors || templates.length > maxTemplates;
  const isConfirmDisabled =
    selectedAuditors.length > maxAuditors ||
    selectedDelegateAdmins.length > maxDelegateAdmins ||
    selectedTemplates.length > maxTemplates ||
    loadingResources ||
    isProcessing;

  const deactivatedAuditors = auditors.length - selectedAuditors.length;
  const deactivatedAdmins = delegateAdmins.length - selectedDelegateAdmins.length;
  const deactivatedTemplates = templates.length - selectedTemplates.length;
  const totalDeactivated = deactivatedAuditors + deactivatedAdmins + deactivatedTemplates;

  const InfoCard = ({ title, children }) => (
    <div className="mb-3 p-3 rounded" style={{ backgroundColor: '#F8F9FA' }}>
      <h6 className="fw-bold mb-3" style={{ fontSize: '13px', color: '#212121' }}>
        {title}
      </h6>
      {children}
    </div>
  );

  InfoCard.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired
  };

  const InfoRow = ({ label, value }) => (
    <div className="d-flex justify-content-between mb-2" style={{ fontSize: '13px' }}>
      <span className="text-muted">{label}</span>
      <span className="fw-bold" style={{ color: '#212121' }}>
        {value}
      </span>
    </div>
  );

  InfoRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  };

  return (
    <Modal
      show={show}
      onHide={compulsory ? () => {} : onHide}
      size="lg"
      centered
      backdrop="static"
      keyboard={!compulsory}
      className="downgrade-modal"
    >
      <Modal.Body className="p-4 p-md-5" style={{ color: '#333333' }}>
        {step === 1 ? (
          <>
            <div className="text-center mb-4">
              <img src={limitReachedIconImg} alt="Warning" style={{ width: 50, height: 50 }} className="mb-3" />
              <h4 className="fw-bold" style={{ color: '#212121' }}>
                {compulsory ? 'Action Required: Plan Limits Exceeded' : `Downgrade To ${targetPlan?.name} Plan`}
              </h4>
              <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                {compulsory ? 'Your subscription plan has been downgraded' : 'Changes take effect at the end of your billing period.'}
              </p>
            </div>

            {/* Warning Banner */}
            <div
              className="d-flex align-items-center p-3 mb-4 rounded"
              role="alert"
              aria-live="polite"
              style={{
                backgroundColor: '#FFF8E6', // Light warning bg
                border: '1px dashed #7D4300',
                color: '#7D4300',
                fontSize: '13px'
              }}
            >
              <FiAlertTriangle size={18} className="me-2 flex-shrink-0" />
              <span>
                {compulsory
                  ? `Your subscription has been downgraded to the ${targetPlan?.name} plan. Please select which resources to keep active before proceeding.`
                  : `Your current ${currentPlan?.name} plan continues until ${moment(currentPeriodEnd).format(
                      'MMMM DD, YYYY'
                    )}. No refund for unused days - downgrade is scheduled only.`}
              </span>
            </div>

            {/* Features Lost Box */}
            <div className="p-3 mb-4 rounded" style={{ backgroundColor: '#F8F9FA' }} aria-live="polite">
              <h6 className="fw-bold mb-3" style={{ fontSize: '14px', color: '#212121' }}>
                Features You&apos;ll Lose:
              </h6>
              <ul className="list-unstyled mb-0" style={{ fontSize: '14px' }}>
                {getLostFeatures().map((feature, idx) => (
                  <li key={idx} className="d-flex align-items-start mb-2">
                    <MdCancel size={18} className="me-2 flex-shrink-0 mt-1" style={{ color: '#666' }} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-center mb-4" style={{ fontSize: '13px', lineHeight: '1.5' }}>
              {targetPlan?.name} plan allows {maxAuditors} auditors,{' '}
              {maxDelegateAdmins > 0 ? `${maxDelegateAdmins} Delegate Admin role & ` : ''}
              {maxTemplates} Templates Cloned. Select which users remain active after downgrade. Unselected users will be deactivated with
              no grace period.
            </p>

            {limitExceeded && (
              <div
                className="d-flex p-3 mb-4 rounded"
                style={{
                  backgroundColor: '#FFF8E6',
                  border: '1px dashed #7D4300',
                  color: '#7D4300',
                  fontSize: '13px'
                }}
              >
                <IoDocumentTextOutline size={18} className="me-2 flex-shrink-0 mt-1" />
                <span>
                  Plan limit exceeded. Deselect the required resources to continue. Unselected resources will be deactivated on the
                  effective date.
                </span>
              </div>
            )}

            {loadingResources ? (
              <div className="text-center py-4">
                <Spinner animation="border" style={{ color: '#005457' }} />
              </div>
            ) : (
              <>
                <CommonSelect
                  label="Auditor Name"
                  name="auditors"
                  // required
                  multiple
                  options={auditors}
                  value={selectedAuditors}
                  onChange={handleAuditorChange}
                  placeholder={auditors.length > 0 ? 'Select auditors' : 'No active auditors found'}
                  description={`You can select up to ${maxAuditors} auditors.`}
                  error={selectedAuditors.length > maxAuditors ? `Maximum ${maxAuditors} allowed` : ''}
                  touched={selectedAuditors.length > maxAuditors}
                />

                {maxDelegateAdmins > 0 && (
                  <CommonSelect
                    label="Delegate Admin"
                    name="delegateAdmins"
                    // required
                    multiple
                    options={delegateAdmins}
                    value={selectedDelegateAdmins}
                    onChange={handleDelegateAdminChange}
                    placeholder={delegateAdmins.length > 0 ? 'Select delegate admin' : 'No active delegate admins found'}
                    description={`You can select up to ${maxDelegateAdmins} delegate admin(s).`}
                    error={selectedDelegateAdmins.length > maxDelegateAdmins ? `Maximum ${maxDelegateAdmins} allowed` : ''}
                    touched={selectedDelegateAdmins.length > maxDelegateAdmins}
                  />
                )}

                <CommonSelect
                  label="Template Name"
                  name="templates"
                  // required
                  multiple
                  options={templates}
                  value={selectedTemplates}
                  onChange={handleTemplateChange}
                  placeholder={templates.length > 0 ? 'Select templates' : 'No active templates found'}
                  description={`You can select up to ${maxTemplates} templates.`}
                  error={selectedTemplates.length > maxTemplates ? `Maximum ${maxTemplates} allowed` : ''}
                  touched={selectedTemplates.length > maxTemplates}
                />
              </>
            )}

            <div className="d-flex mt-4 pt-2" style={{ gap: '16px' }}>
              {!compulsory && (
                <CommonButton
                  variant="secondary"
                  className="flex-fill"
                  onClick={onHide}
                  disabled={isProcessing}
                  style={{ padding: '10px' }}
                >
                  Cancel
                </CommonButton>
              )}
              {onUpgradeToPreviousPlan && (
                <CommonButton
                  variant="outline-primary"
                  className="flex-fill"
                  onClick={onUpgradeToPreviousPlan}
                  disabled={isProcessing}
                  style={{ padding: '10px', whiteSpace: 'nowrap' }}
                >
                  Continue with Previous Plan
                </CommonButton>
              )}
              <CommonButton
                variant="primary"
                className="flex-fill"
                onClick={handleReviewDowngrade}
                disabled={isConfirmDisabled}
                loading={isProcessing}
                style={{ padding: '10px' }}
              >
                Review Downgrade
              </CommonButton>
            </div>
          </>
        ) : (
          <>
            <div className="text-center mb-4">
              <IoShieldCheckmarkOutline size={48} color="#005457" className="mb-3" />
              <h4 className="fw-bold" style={{ color: '#212121' }}>
                {compulsory ? 'Review & Apply Changes' : 'Review & Confirm Downgrade'}
              </h4>
              <p className="text-muted mx-auto" style={{ fontSize: '13px', maxWidth: '450px', lineHeight: '1.5' }}>
                {compulsory
                  ? `Review the changes below. Selected resources will remain active under the ${targetPlan?.name} plan. Unselected resources will be deactivated immediately.`
                  : `Review the changes below. Once the downgrade becomes active, deactivated resources will no longer have access under the ${targetPlan?.name}. Previously deactivated resources will not be restored automatically if you upgrade again in the future.`}
              </p>
            </div>

            {totalDeactivated > 0 && (
              <div
                className="d-flex align-items-center p-3 mb-4 rounded"
                role="alert"
                aria-live="polite"
                style={{
                  backgroundColor: '#FFF8E6',
                  border: '1px dashed #7D4300',
                  color: '#7D4300',
                  fontSize: '12px'
                }}
              >
                <FiAlertTriangle size={16} className="me-2 flex-shrink-0" />
                <span>
                  {compulsory
                    ? `${totalDeactivated} resource(s) will be permanently deactivated immediately. In-progress and unsubmitted audits for deactivated users will be removed with no grace period.`
                    : `${totalDeactivated} resource(s) will be permanently deactivated on ${moment(currentPeriodEnd).format(
                        'DD MMM YYYY'
                      )}. In-progress and unsubmitted audits for deactivated users will be removed with no grace period.`}
                </span>
              </div>
            )}

            {compulsory ? (
              <InfoCard title="Plan Transition:">
                <InfoRow label="Previous Plan" value={currentPlan?.name} />
                <InfoRow label="New Plan" value={targetPlan?.name} />
              </InfoCard>
            ) : (
              <InfoCard title="Proration Breakdown:">
                <InfoRow label="From" value={currentPlan?.name} />
                <InfoRow label="To" value={targetPlan?.name} />
                <div className="d-flex justify-content-between mt-2 pt-2" style={{ borderTop: '1px solid #E5E7EB', fontSize: '13px' }}>
                  <span className="text-muted">Effective Date</span>
                  <span className="fw-bold text-dark">{moment(currentPeriodEnd).format('MMM DD, YYYY')}</span>
                </div>
              </InfoCard>
            )}

            <InfoCard title="Auditors:">
              <InfoRow label="Auditors Retained" value={selectedAuditors.length} />
              <InfoRow label="Auditors Deactivated" value={deactivatedAuditors} />
            </InfoCard>

            <InfoCard title="Delegate Admin:">
              <InfoRow label="Delegate Admin Retained" value={selectedDelegateAdmins.length} />
              <InfoRow label="Delegate Admin Deactivated" value={deactivatedAdmins} />
            </InfoCard>

            <InfoCard title="Templates:">
              <InfoRow label="Templates Retained" value={selectedTemplates.length} />
              <InfoRow label="Templates Deactivated" value={deactivatedTemplates} />
            </InfoCard>

            <div className="text-center mb-4 mt-2">
              <span className="text-muted" style={{ fontSize: '12px' }}>
                Custom templates & chapters/sections not supported on {targetPlan?.name}
              </span>
            </div>

            <div className="d-flex mt-4 pt-2" style={{ gap: '16px' }}>
              <CommonButton
                variant="secondary"
                className="flex-fill"
                onClick={() => setStep(1)}
                disabled={isProcessing}
                style={{ padding: '10px' }}
              >
                Back
              </CommonButton>
              <CommonButton
                variant="primary"
                className="flex-fill"
                onClick={handleConfirm}
                loading={isProcessing}
                style={{ padding: '10px' }}
              >
                {compulsory ? 'Confirm & Apply Selections' : 'Schedule Downgrade'}
              </CommonButton>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

DowngradeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  targetPlan: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  }),
  currentPlan: PropTypes.shape({
    name: PropTypes.string
  }),
  currentPeriodEnd: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  compulsory: PropTypes.bool,
  onUpgradeToPreviousPlan: PropTypes.func
};

export default DowngradeModal;
